// Dependency-free i18n for the guest-facing deposit flow (DepositView +
// DepositPaymentForm). Scope is deliberately narrow: guests are UAE renters who
// need Arabic + RTL. The merchant dashboard, admin, and marketing pages stay
// English-only — a much larger job with app-wide RTL CSS implications.
//
// dir/lang are applied to the .deposit-view container, NOT <html>: the deposit
// route renders inside the shared app shell (marketing header, cookie banner),
// and flipping <html> would drag that English chrome into RTL. Scoping to the
// container keeps RTL where it belongs and needs no mount/unmount cleanup.
//
// Language STATE lives in the shared LocaleProvider (src/i18n/LocaleProvider.jsx)
// so the header LanguageSwitcher and this flow agree; this module owns detection,
// formatting, and the string dictionary.
// ponytail: no i18n library for two languages on one flow; a dict is it.

export const LOCALES = ['en', 'ar']
export const LOCALE_STORAGE_KEY = 'akara-locale'

// Display metadata for the language pickers (header switcher + mobile menu).
export const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
]

// Query param wins (a merchant can share an Arabic link), then the guest's
// stored choice, then the browser language, then English.
export function detectLocale(search) {
  const params = new URLSearchParams(search || '')
  const q = params.get('lang')
  if (q && LOCALES.includes(q)) return q
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && LOCALES.includes(stored)) return stored
  } catch {
    /* storage blocked — fall through */
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const nav = navigator.language.slice(0, 2)
    if (LOCALES.includes(nav)) return nav
  }
  return 'en'
}

export const localeDir = (locale) => (locale === 'ar' ? 'rtl' : 'ltr')

// BCP-47 tags for Intl (money + dates). ar-AE renders Arabic-Indic digits and
// the local currency form; en-GB keeps the existing dd-mmm-yyyy date shape.
export const moneyLocale = (locale) => (locale === 'ar' ? 'ar-AE' : 'en-AE')
export const dateLocale = (locale) => (locale === 'ar' ? 'ar-AE' : 'en-GB')

export function formatGuestDate(value, locale, opts) {
  try {
    return new Date(value).toLocaleDateString(dateLocale(locale), opts)
  } catch {
    return value
  }
}

const STRINGS = {
  en: {
    loading: 'Loading deposit details…',
    notFoundTitle: 'Request not found',
    notFoundError: 'This deposit request was not found or the link has expired.',
    goToAkara: 'Go to Akara',
    title: 'Security Deposit Request',
    // "**{merchant}** has requested a security deposit from you."
    requestIntroBefore: '',
    requestIntroAfter: ' has requested a security deposit from you.',
    amount: 'Amount',
    heldUntil: 'Held until',
    heldDays: (n) => `${n} days`,
    status: 'Status',
    guest: 'Guest',
    date: 'Date',
    linkValidUntil: 'Link valid until',
    depositStatus: 'Deposit status',
    currentStatus: 'Current status',
    steps: ['Deposit requested', 'Card authorized', 'Deposit settled'],
    statusNames: {
      Pending: 'Pending',
      Failed: 'Failed',
      Active: 'Active',
      Disputed: 'Disputed',
      Claimed: 'Claimed',
      Released: 'Released',
    },
    statusCaptions: {
      Pending: 'Awaiting your authorization.',
      Failed: 'The last authorization attempt failed — you can try again below.',
      Active: 'Your card is authorized and held for the merchant.',
      Disputed: 'You disputed this request. The merchant is reviewing it.',
      Claimed: 'The merchant has claimed against this deposit — see the breakdown below.',
      Released: 'This deposit has been released. No charge was taken.',
    },
    statusCaptionFallback: (s) => `Current status: ${s}.`,
    landlordDeductions: 'Landlord deductions',
    noDeductions: 'No deductions have been raised for this deposit.',
    totalDeducted: 'Total deducted',
    evidence: 'Evidence',
    damagePhotoAlt: 'Damage photo',
    invoiceLabel: 'Invoice',
    attachmentLabel: 'Attachment',
    needEscalate: 'Need to escalate?',
    escalateDesc: 'Send your side of the dispute to Akara for an independent review.',
    escalateLabel: 'Explain why you’re escalating',
    escalatePlaceholder:
      "Describe what happened and why you disagree with the merchant's claim.",
    attachPhotos: 'Attach photos (optional)',
    photosAttached: (n) => `${n} photo${n > 1 ? 's' : ''} attached`,
    escalateButton: 'Escalate to Akara',
    sending: 'Sending…',
    escalateRequired: 'Please describe why you are escalating so Akara can review it.',
    escalatedNote:
      'Akara’s team has your statement and will review the dispute. You’ll be emailed the outcome.',
    failedRetry: 'Previous payment could not be completed. You can try again below.',
    paymentCancelled: 'Payment was cancelled. You can approve and pay when ready.',
    authorizedTitle: 'Deposit authorized',
    disputedTitle: 'Deposit disputed',
    authorizedBody:
      'Thank you! Your card has been authorized for the deposit. The merchant has been notified.',
    disputedBody:
      'Your dispute has been submitted. The merchant will review your concern.',
    approveButton: 'Approve and authorize card',
    disputeButton: 'Dispute',
    disputeInstead: 'This isn’t right — dispute this request instead',
    alreadyProcessed: (statusName) => ({
      before: 'This deposit has already been ',
      status: statusName.toLowerCase(),
      after: '.',
    }),
    footerPoweredBefore: 'Powered by ',
    footerPoweredAfter: ' — Security deposits made simple.',
    footerQuestions: 'Questions? ',
    // DepositPaymentForm
    howCardUsed: 'How your card is used',
    disclosureMid: ' is requesting a security hold of ',
    disclosureAfter:
      '. Your card is only imprinted as a security hold — no money is debited now. The amount is captured only if the merchant later files a claim, and the hold is released otherwise.',
    authorizeHold: (amount) => `Authorize ${amount} hold`,
    authorizing: 'Authorizing…',
    secureNote:
      'Card details are handled securely by our PCI-DSS certified payment provider. Akara never sees or stores your card number.',
    preparingPayment: 'Preparing secure payment…',
    notConfigured:
      'Card payments are not available right now. Please contact the merchant.',
  },
  ar: {
    loading: 'جارٍ تحميل تفاصيل الوديعة…',
    notFoundTitle: 'الطلب غير موجود',
    notFoundError: 'لم يتم العثور على طلب الوديعة أو أن الرابط منتهي الصلاحية.',
    goToAkara: 'الذهاب إلى Akara',
    title: 'طلب وديعة ضمان',
    requestIntroBefore: 'لقد طلب منك ',
    requestIntroAfter: ' تقديم وديعة ضمان.',
    amount: 'المبلغ',
    heldUntil: 'محجوز حتى',
    heldDays: (n) => `${n} يومًا`,
    status: 'الحالة',
    guest: 'الضيف',
    date: 'التاريخ',
    linkValidUntil: 'الرابط صالح حتى',
    depositStatus: 'حالة الوديعة',
    currentStatus: 'الحالة الحالية',
    steps: ['تم طلب الوديعة', 'تم تفويض البطاقة', 'تمت تسوية الوديعة'],
    statusNames: {
      Pending: 'قيد الانتظار',
      Failed: 'فشل',
      Active: 'نشِط',
      Disputed: 'متنازع عليه',
      Claimed: 'تمت المطالبة',
      Released: 'تم الإفراج',
    },
    statusCaptions: {
      Pending: 'بانتظار موافقتك.',
      Failed: 'فشلت آخر محاولة تفويض — يمكنك المحاولة مرة أخرى أدناه.',
      Active: 'تم تفويض بطاقتك وحجزها لصالح التاجر.',
      Disputed: 'لقد اعترضت على هذا الطلب. يقوم التاجر بمراجعته.',
      Claimed: 'قدّم التاجر مطالبة على هذه الوديعة — انظر التفاصيل أدناه.',
      Released: 'تم الإفراج عن هذه الوديعة. لم يُخصم أي مبلغ.',
    },
    statusCaptionFallback: (s) => `الحالة الحالية: ${s}.`,
    landlordDeductions: 'خصومات المالك',
    noDeductions: 'لم يتم تسجيل أي خصومات على هذه الوديعة.',
    totalDeducted: 'إجمالي المخصوم',
    evidence: 'الأدلة',
    damagePhotoAlt: 'صورة الضرر',
    invoiceLabel: 'فاتورة',
    attachmentLabel: 'مرفق',
    needEscalate: 'بحاجة إلى التصعيد؟',
    escalateDesc: 'أرسل وجهة نظرك في النزاع إلى Akara لمراجعة مستقلة.',
    escalateLabel: 'اشرح سبب التصعيد',
    escalatePlaceholder: 'صِف ما حدث ولماذا لا توافق على مطالبة التاجر.',
    attachPhotos: 'إرفاق صور (اختياري)',
    photosAttached: (n) => `تم إرفاق ${n} صورة`,
    escalateButton: 'التصعيد إلى Akara',
    sending: 'جارٍ الإرسال…',
    escalateRequired: 'يرجى توضيح سبب التصعيد حتى تتمكن Akara من مراجعته.',
    escalatedNote:
      'استلم فريق Akara إفادتك وسيراجع النزاع. سيتم إبلاغك بالنتيجة عبر البريد الإلكتروني.',
    failedRetry: 'تعذّر إتمام الدفعة السابقة. يمكنك المحاولة مرة أخرى أدناه.',
    paymentCancelled: 'تم إلغاء الدفع. يمكنك الموافقة والدفع عندما تكون جاهزًا.',
    authorizedTitle: 'تم تفويض الوديعة',
    disputedTitle: 'تم الاعتراض على الوديعة',
    authorizedBody: 'شكرًا لك! تم تفويض بطاقتك للوديعة. تم إبلاغ التاجر.',
    disputedBody: 'تم تقديم اعتراضك. سيقوم التاجر بمراجعته.',
    approveButton: 'الموافقة وتفويض البطاقة',
    disputeButton: 'اعتراض',
    disputeInstead: 'هذا غير صحيح — اعترض على هذا الطلب بدلاً من ذلك',
    alreadyProcessed: (statusName) => ({
      before: 'هذه الوديعة ',
      status: statusName,
      after: ' بالفعل.',
    }),
    footerPoweredBefore: 'بدعم من ',
    footerPoweredAfter: ' — الودائع الأمنية بكل بساطة.',
    footerQuestions: 'أسئلة؟ ',
    howCardUsed: 'كيف تُستخدم بطاقتك',
    disclosureMid: ' يطلب حجز ضمان بقيمة ',
    disclosureAfter:
      '. تُستخدم بطاقتك فقط كحجز ضمان — لا يُخصم أي مبلغ الآن. يُخصم المبلغ فقط إذا قدّم التاجر مطالبة لاحقًا، ويُفرج عن الحجز خلاف ذلك.',
    authorizeHold: (amount) => `تفويض حجز ${amount}`,
    authorizing: 'جارٍ التفويض…',
    secureNote:
      'تتم معالجة تفاصيل البطاقة بأمان عبر مزوّد دفع معتمد وفق معيار PCI-DSS. لا ترى Akara رقم بطاقتك ولا تخزّنه أبدًا.',
    preparingPayment: 'جارٍ تجهيز الدفع الآمن…',
    notConfigured: 'المدفوعات بالبطاقة غير متاحة حاليًا. يرجى التواصل مع التاجر.',
  },
}

export const getStrings = (locale) => STRINGS[locale] || STRINGS.en
